import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield, Mail, Phone, MessageCircle, RefreshCw, LogOut, ChevronDown, ChevronUp,
  Filter, Loader2, Inbox, MapPin, Calendar, Users, Sparkles, ArrowRight,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  new: { label: "NEW", color: "#C16542", bg: "rgba(193,101,66,0.12)" },
  replied: { label: "REPLIED", color: "#A07042", bg: "rgba(160,112,66,0.14)" },
  confirmed: { label: "CONFIRMED", color: "#2F7D3F", bg: "rgba(47,125,63,0.14)" },
  archived: { label: "ARCHIVED", color: "#5C5248", bg: "rgba(92,82,72,0.14)" },
};

const SOURCE_LABEL = {
  "booking-page": "Detailed booking",
  "classic-booking": "Classic booking",
  "spa-request": "Spa request",
  "experience-request": "Experience request",
  "contact-form": "Contact form",
  other: "Other",
};

const SOURCE_ENTRIES = Object.entries(SOURCE_LABEL).filter(([k]) => k !== "other");

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const cleanPhone = (p) => (p ? p.replace(/[^\d+]/g, "") : "");

// ───────────────────────────────────────────────────────────────────────────
// Cookie-based login gate. Uses the shared `/api/admin/session/login`
// endpoint — the same one that unlocks the CMS admin — instead of the old
// HTTPBasic flow that stored base64 credentials in sessionStorage.
const LoginGate = ({ onAuth }) => {
  const [p, setP] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const password = (p || "").trim();
      if (!password) {
        toast.error("Please enter the access code");
        return;
      }
      await axios.post(`${API}/admin/session/login`, { password });
      // Server has set the httpOnly cookie. No client-side storage.
      onAuth();
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error("Too many attempts. Try again in a few minutes.");
      } else {
        toast.error("Incorrect access code");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] bg-[#FDFBF7] flex items-center justify-center px-6 py-20" data-testid="admin-login-gate">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8 text-[10px] tracking-[0.3em] uppercase text-[#A07042]">
          <Shield className="w-3.5 h-3.5" strokeWidth={1.6} />
          Xaluca · Admin
        </div>
        <h1 className="font-serif-x text-4xl md:text-5xl tracking-tight leading-[1.05] text-[#2C2621]">
          Enquiry <em className="not-italic text-[#C16542]">queue</em>.
        </h1>
        <p className="mt-4 text-sm text-[#5C5248] leading-relaxed">
          For internal use. All booking, contact, spa and experience enquiries are aggregated here in real time.
        </p>
        <form onSubmit={submit} className="mt-10 space-y-4">
          <label className="flex flex-col">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248] mb-2">Access code</span>
            <input
              data-testid="admin-login-password"
              type="password"
              value={p}
              onChange={(e) => setP(e.target.value)}
              autoComplete="current-password"
              autoFocus
              required
              className="w-full border border-[#2C2621]/15 bg-[#FDFBF7] px-4 py-3 text-[15px] focus:outline-none focus:border-[#C16542]"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-3 bg-[#2C2621] hover:bg-[#1A1513] disabled:opacity-60 text-[#FDFBF7] px-7 py-4 text-[11px] tracking-[0.3em] uppercase transition-colors"
            data-testid="admin-login-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />}
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────────────
const EnquiryRow = ({ enquiry, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const status = STATUS_META[enquiry.status] || STATUS_META.new;
  const wa = cleanPhone(enquiry.whatsapp || enquiry.phone);
  const extraEntries = useMemo(
    () => (enquiry.extras ? Object.entries(enquiry.extras).filter(([, v]) => Boolean(v)) : []),
    [enquiry.extras]
  );

  const setStatus = async (next) => {
    setUpdating(true);
    try {
      await axios.patch(`${API}/admin/enquiries/${enquiry.id}/status`, { status: next });
      onUpdate(enquiry.id, next);
      toast.success(`Marked ${STATUS_META[next].label.toLowerCase()}`);
    } catch {
      toast.error("Update failed");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <article
      className="bg-[#FDFBF7] border border-[#2C2621]/10 hover:border-[#D4A373]/45 transition-colors"
      data-testid={`admin-enquiry-${enquiry.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-4"
        data-testid={`admin-enquiry-toggle-${enquiry.id}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className="text-[10px] tracking-[0.3em] uppercase px-2.5 py-1 font-medium"
              style={{ color: status.color, background: status.bg }}
              data-testid={`admin-enquiry-status-${enquiry.id}`}
            >
              {status.label}
            </span>
            <span className="text-[10px] tracking-[0.25em] uppercase text-[#A07042]">
              {SOURCE_LABEL[enquiry.source] || enquiry.source}
            </span>
            <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]/65">
              {formatDate(enquiry.created_at)}
            </span>
            {enquiry.items && enquiry.items.length > 0 && (
              <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]/70">
                {enquiry.items.length} item{enquiry.items.length > 1 ? "s" : ""}
              </span>
            )}
          </div>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <span className="font-serif-x text-xl text-[#2C2621]">
              {enquiry.full_name || "—"}
            </span>
            {enquiry.hotel && (
              <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase text-[#5C5248]">
                <MapPin className="w-3 h-3" strokeWidth={1.5} />
                {enquiry.hotel}
              </span>
            )}
            {enquiry.check_in && (
              <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase text-[#5C5248]">
                <Calendar className="w-3 h-3" strokeWidth={1.5} />
                {enquiry.check_in}{enquiry.check_out ? ` → ${enquiry.check_out}` : ""}
              </span>
            )}
            {typeof enquiry.guests === "number" && enquiry.guests > 0 && (
              <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase text-[#5C5248]">
                <Users className="w-3 h-3" strokeWidth={1.5} />
                {enquiry.guests} guest{enquiry.guests > 1 ? "s" : ""}
              </span>
            )}
          </div>
          {enquiry.email && (
            <p className="mt-2 text-[12px] tracking-[0.05em] text-[#5C5248]">{enquiry.email}</p>
          )}
        </div>
        <div className="shrink-0 flex items-center gap-2">
          {open ? <ChevronUp className="w-4 h-4 text-[#5C5248]" strokeWidth={1.5} /> : <ChevronDown className="w-4 h-4 text-[#5C5248]" strokeWidth={1.5} />}
        </div>
      </button>

      {open && (
        <div className="border-t border-[#2C2621]/10 p-5 md:p-6 space-y-6 bg-[#FDFBF7]" data-testid={`admin-enquiry-body-${enquiry.id}`}>
          {enquiry.message && (
            <div>
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248]/65">Message</span>
              <p className="mt-2 text-sm text-[#2C2621] leading-relaxed whitespace-pre-wrap">
                {enquiry.message}
              </p>
            </div>
          )}

          {enquiry.items && enquiry.items.length > 0 && (
            <div>
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248]/65">Requested items</span>
              <ul className="mt-3 space-y-2">
                {enquiry.items.map((it, i) => (
                  <li key={`it-${enquiry.id}-${i}`} className="text-sm text-[#2C2621] flex items-start gap-2">
                    <Sparkles className="w-3 h-3 mt-1 text-[#C16542]" strokeWidth={1.5} />
                    <span>
                      {typeof it === "string" ? it : (it.label || it.name || JSON.stringify(it))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {extraEntries.length > 0 && (
            <div>
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248]/65">Extras</span>
              <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {extraEntries.map(([k, v]) => (
                  <div key={`ex-${enquiry.id}-${k}`} className="flex items-baseline gap-3">
                    <dt className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]/70 min-w-[110px]">{k}</dt>
                    <dd className="text-[#2C2621]">{typeof v === "string" ? v : JSON.stringify(v)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 pt-4 border-t border-[#2C2621]/10">
            <div className="flex items-center gap-2 flex-wrap" data-testid={`admin-contact-actions-${enquiry.id}`}>
              {enquiry.email && (
                <a
                  href={`mailto:${enquiry.email}`}
                  className="inline-flex items-center gap-2 border border-[#2C2621]/25 hover:border-[#2C2621] text-[#2C2621] px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase transition-colors"
                  data-testid={`admin-email-${enquiry.id}`}
                >
                  <Mail className="w-3.5 h-3.5" strokeWidth={1.5} />Email
                </a>
              )}
              {enquiry.phone && (
                <a
                  href={`tel:${cleanPhone(enquiry.phone)}`}
                  className="inline-flex items-center gap-2 border border-[#2C2621]/25 hover:border-[#2C2621] text-[#2C2621] px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase transition-colors"
                  data-testid={`admin-phone-${enquiry.id}`}
                >
                  <Phone className="w-3.5 h-3.5" strokeWidth={1.5} />Call
                </a>
              )}
              {wa && (
                <a
                  href={`https://wa.me/${wa}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border border-[#2F7D3F]/50 hover:border-[#2F7D3F] text-[#2F7D3F] px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase transition-colors"
                  data-testid={`admin-whatsapp-${enquiry.id}`}
                >
                  <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} />WhatsApp
                </a>
              )}
            </div>

            <div className="flex items-center gap-2" data-testid={`admin-status-actions-${enquiry.id}`}>
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248]/65">Set status</span>
              {["new", "replied", "confirmed", "archived"].map((s) => {
                const meta = STATUS_META[s];
                const active = enquiry.status === s;
                return (
                  <button
                    key={`st-${enquiry.id}-${s}`}
                    type="button"
                    onClick={() => setStatus(s)}
                    disabled={updating || active}
                    className={`text-[10px] tracking-[0.25em] uppercase px-2.5 py-1.5 border transition-colors ${
                      active ? "text-[#FDFBF7] border-transparent" : "text-[#5C5248] border-[#2C2621]/20 hover:border-[#2C2621]/60"
                    }`}
                    style={active ? { background: meta.color, borderColor: meta.color } : {}}
                    data-testid={`admin-set-status-${s}-${enquiry.id}`}
                  >
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

// ───────────────────────────────────────────────────────────────────────────
const AdminEnquiriesPage = () => {
  // null = still checking cookie, false = not authed, true = authed
  const [authState, setAuthState] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/enquiries`);
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthState(false);
        toast.error("Session expired — please sign in again");
      } else {
        setError("Failed to load enquiries");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Verify existing cookie session on mount.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/admin/session/verify`)
      .then(() => { if (!cancelled) setAuthState(true); })
      .catch(() => { if (!cancelled) setAuthState(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (authState === true) load();
  }, [authState, load]);

  const onLocalStatusUpdate = (id, status) => {
    setData((d) => d ? { ...d, enquiries: d.enquiries.map((e) => e.id === id ? { ...e, status } : e) } : d);
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.enquiries.filter((e) => {
      if (sourceFilter !== "all" && (e.source || "other") !== sourceFilter) return false;
      if (statusFilter !== "all" && (e.status || "new") !== statusFilter) return false;
      return true;
    });
  }, [data, sourceFilter, statusFilter]);

  const signOut = async () => {
    try {
      await axios.post(`${API}/admin/session/logout`);
    } catch { /* ignore */ }
    setAuthState(false);
    setData(null);
  };

  if (authState === null) {
    return (
      <div className="bg-[#F2EBE1]/40 min-h-[80vh] flex items-center justify-center text-[#5C5248]" data-testid="admin-enquiries-checking">
        <Loader2 className="w-5 h-5 animate-spin mr-3" /> Checking access…
      </div>
    );
  }

  return (
    <div className="bg-[#F2EBE1]/40 min-h-screen" data-testid="page-admin-enquiries">
      {authState !== true ? (
        <LoginGate onAuth={() => setAuthState(true)} />
      ) : (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          {/* Header */}
          <header className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#A07042]">
                <Shield className="w-3.5 h-3.5" strokeWidth={1.6} />
                Xaluca · Admin · Enquiries
              </div>
              <h1 className="mt-4 font-serif-x text-4xl md:text-5xl tracking-tight leading-[1.05] text-[#2C2621]">
                Inbox · <em className="not-italic text-[#C16542]">{data ? data.total : "…"}</em>
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => load()}
                disabled={loading}
                className="inline-flex items-center gap-2 border border-[#2C2621]/25 hover:border-[#2C2621] text-[#2C2621] px-4 py-2.5 text-[10px] tracking-[0.3em] uppercase transition-colors disabled:opacity-50"
                data-testid="admin-refresh"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />}
                Refresh
              </button>
              <button
                type="button"
                onClick={signOut}
                className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#5C5248] hover:text-[#C16542] transition-colors"
                data-testid="admin-signout"
              >
                <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
                Sign out
              </button>
            </div>
          </header>

          {/* Stats strip */}
          {data && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#2C2621]/10 border border-[#2C2621]/10 mb-10" data-testid="admin-stats">
              {SOURCE_ENTRIES.map(([k, label]) => (
                <button
                  type="button"
                  key={`stat-${k}`}
                  onClick={() => setSourceFilter(sourceFilter === k ? "all" : k)}
                  className={`bg-[#FDFBF7] p-5 text-left transition-colors ${sourceFilter === k ? "bg-[#F2EBE1]" : "hover:bg-[#F2EBE1]/60"}`}
                  data-testid={`admin-stat-${k}`}
                >
                  <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]/70 leading-tight block">{label}</span>
                  <span className="font-serif-x text-3xl text-[#2C2621] mt-2 block tabular-nums">{data.by_source[k] || 0}</span>
                </button>
              ))}
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8 pb-6 border-b border-[#2C2621]/10" data-testid="admin-filters">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#5C5248]"><Filter className="w-3.5 h-3.5" strokeWidth={1.5} />Status</span>
              {["all", "new", "replied", "confirmed", "archived"].map((s) => (
                <button
                  key={`fst-${s}`}
                  type="button"
                  onClick={() => setStatusFilter(s)}
                  className={`text-[10px] tracking-[0.25em] uppercase px-3 py-1.5 border transition-colors ${
                    statusFilter === s ? "bg-[#2C2621] text-[#FDFBF7] border-[#2C2621]" : "border-[#2C2621]/20 hover:border-[#2C2621]/60 text-[#5C5248]"
                  }`}
                  data-testid={`admin-filter-status-${s}`}
                >
                  {s === "all" ? "All" : STATUS_META[s].label}
                </button>
              ))}
            </div>
            {sourceFilter !== "all" && (
              <button
                type="button"
                onClick={() => setSourceFilter("all")}
                className="text-[10px] tracking-[0.3em] uppercase text-[#C16542] hover:text-[#A35133]"
                data-testid="admin-clear-source-filter"
              >
                Clear source filter ({SOURCE_LABEL[sourceFilter]}) ✕
              </button>
            )}
          </div>

          {/* List */}
          {error && <p className="text-sm text-[#C16542]">{error}</p>}
          {!data && loading && (
            <div className="flex items-center justify-center py-32 text-[#5C5248]"><Loader2 className="w-5 h-5 animate-spin mr-3" />Loading…</div>
          )}
          {data && filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-24 text-[#5C5248]/85" data-testid="admin-empty">
              <Inbox className="w-10 h-10 text-[#A07042] mb-4" strokeWidth={1.2} />
              <p className="font-serif-x text-2xl text-[#2C2621]">No enquiries match these filters</p>
              <p className="text-sm mt-2">Try clearing the source or status filter above.</p>
            </div>
          )}
          {data && filtered.length > 0 && (
            <div className="space-y-3" data-testid="admin-enquiry-list">
              {filtered.map((e) => (
                <EnquiryRow key={`row-${e.id}`} enquiry={e} onUpdate={onLocalStatusUpdate} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminEnquiriesPage;
